/**
 * Invoice IDP Upload Modal — drag-drop a PDF, hit Claude, render structured fields.
 *
 * Opened from the TopBar `invoice-upload-trigger` button. Fully bilingual via the
 * usual pt.* proxy. The modal owns its own state — closing dismisses the result
 * (users can re-upload to get a fresh extraction).
 */
import React, { useCallback, useRef, useState } from "react";
import { Upload, FileText, X, CheckCircle2, AlertCircle, Copy, Download, TicketCheck, Loader2 } from "lucide-react";
import { apiPostFormData, apiPost } from "@/lib/api";
import pt from "@/lib/i18n";
import { useNavigate } from "react-router-dom";

const MAX_BYTES = 10 * 1024 * 1024;

function _fmtMoney(value, currency) {
    if (typeof value !== "number") return "—";
    const intl = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${currency || ""} ${intl.format(value)}`.trim();
}

function _StatusPill({ flags }) {
    const errors = (flags || []).filter((f) => f.severity === "error").length;
    const warnings = (flags || []).filter((f) => f.severity === "warning").length;
    if (errors === 0 && warnings === 0) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider"
                  style={{ background: "rgba(16, 185, 129, 0.12)", color: "#047857" }}>
                <CheckCircle2 size={12} /> {pt.invoiceModal?.statusOk || "Clean"}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider"
              style={{ background: "rgba(217, 119, 6, 0.14)", color: "#b45309" }}>
            <AlertCircle size={12} /> {errors > 0 ? (pt.invoiceModal?.statusError || "Error") : (pt.invoiceModal?.statusWarning || "Warning")} ({errors + warnings})
        </span>
    );
}

function _KV({ label, value, mono = false, testId }) {
    return (
        <div className="space-y-0.5" data-testid={testId}>
            <p className="font-body text-[10px] uppercase tracking-[0.16em] text-[color:var(--slate-light)]">
                {label}
            </p>
            <p className={"text-sm text-[color:var(--slate-deep)] break-words " + (mono ? "font-mono" : "")}>
                {value || "—"}
            </p>
        </div>
    );
}

export const InvoiceUploadModal = ({ open, onClose }) => {
    const fileInputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [creatingTicket, setCreatingTicket] = useState(false);
    const navigate = useNavigate();

    const resetAll = useCallback(() => {
        setFile(null); setUploading(false); setProgress(0); setResult(null);
        setError(""); setCreatingTicket(false);
    }, []);

    const handleClose = () => { resetAll(); onClose?.(); };

    const validate = (f) => {
        if (!f) return pt.invoiceModal?.errPickFile || "Pick a PDF first.";
        if (!f.name.toLowerCase().endsWith(".pdf")) return pt.invoiceModal?.errOnlyPdf || "Only PDF files are accepted.";
        if (f.size > MAX_BYTES) return pt.invoiceModal?.errTooLarge || "File exceeds the 10 MB limit.";
        if (f.size === 0) return pt.invoiceModal?.errEmpty || "File is empty.";
        return null;
    };

    const startUpload = async (chosen) => {
        const v = validate(chosen);
        if (v) { setError(v); return; }
        setError(""); setFile(chosen); setUploading(true); setProgress(0); setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", chosen);
            // Simulated determinate progress while the network request is in flight
            const tick = setInterval(() => setProgress((p) => Math.min(92, p + Math.random() * 9)), 280);
            const res = await apiPostFormData("/documents/upload-invoice", fd);
            clearInterval(tick);
            setProgress(100);
            setResult(res);
        } catch (e) {
            setError(e?.message || (pt.invoiceModal?.errExtract || "Extraction failed."));
        } finally {
            setUploading(false);
            setTimeout(() => setProgress(0), 600);
        }
    };

    const onDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) startUpload(f);
    };

    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (f) startUpload(f);
    };

    const copyJson = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(result?.extracted ?? {}, null, 2));
        } catch {
            /* ignore */
        }
    };

    const downloadJson = () => {
        const blob = new Blob([JSON.stringify(result?.extracted ?? {}, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = (file?.name?.replace(/\.pdf$/i, "") || "invoice") + ".json";
        a.click();
        URL.revokeObjectURL(url);
    };

    const createTicket = async () => {
        if (!result?.id) return;
        setCreatingTicket(true);
        try {
            const t = await apiPost(`/documents/invoices/${result.id}/create-ticket`, {});
            // Update local result so the button flips to "View ticket"
            setResult((r) => ({ ...r, ticket_id: t.id, ticket_number: t.ticket_number }));
        } catch (e) {
            setError(e?.message || (pt.invoiceModal?.errCreateTicket || "Could not create ticket."));
        } finally {
            setCreatingTicket(false);
        }
    };

    const openTicket = () => {
        const tid = result?.ticket_id;
        if (!tid) return;
        handleClose();
        navigate(`/tickets/${tid}`);
    };

    if (!open) return null;

    const extracted = result?.extracted || {};
    const flags = result?.flags || [];
    const lineItems = extracted.line_items || [];

    return (
        <div
            className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
            style={{
                background: "rgba(15, 12, 8, 0.62)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                zIndex: 9999,
            }}
            data-testid="invoice-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
            <div
                className="w-full max-w-4xl rounded-2xl shadow-2xl my-auto"
                style={{
                    background: "var(--bg-base, #faf7f2)",
                    border: "1px solid var(--glass-border, rgba(0,0,0,0.08))",
                    maxHeight: "92vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
                data-testid="invoice-modal"
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-4 px-6 py-4 shrink-0"
                     style={{ borderBottom: "1px solid var(--glass-border, rgba(0,0,0,0.08))",
                              background: "var(--bg-base, #faf7f2)" }}>
                    <div className="flex items-center gap-3">
                        <span className="grid place-items-center w-9 h-9 rounded-lg"
                              style={{ background: "var(--copper-subtle, rgba(200,121,65,0.10))" }}>
                            <FileText size={18} className="text-[color:var(--copper,#c87941)]" />
                        </span>
                        <div>
                            <h3 className="font-display text-lg text-[color:var(--slate-deep,#1F2D40)]">
                                {pt.invoiceModal?.title || "Upload Invoice"}
                            </h3>
                            <p className="font-body text-xs text-[color:var(--slate-mid,#5B6B7E)]">
                                {pt.invoiceModal?.subtitle || "PDF · 10 MB max · structured extraction by Claude"}
                            </p>
                        </div>
                    </div>
                    <button onClick={handleClose} data-testid="invoice-modal-close"
                            className="p-2 rounded-full hover:bg-[color:var(--copper-subtle,rgba(200,121,65,0.10))] transition"
                            aria-label="Close">
                        <X size={18} className="text-[color:var(--slate-mid,#5B6B7E)]" />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto" style={{ background: "var(--bg-base, #faf7f2)" }}>
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                        {/* Left: dropzone */}
                        <div className="lg:col-span-2 p-6 space-y-4"
                             style={{ borderRight: "1px solid var(--glass-border, rgba(0,0,0,0.08))",
                                      background: "var(--bg-base, #faf7f2)" }}>
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={onDrop}
                                data-testid="invoice-dropzone"
                                className="cursor-pointer rounded-xl p-8 text-center transition-colors"
                                style={{
                                    background: dragOver
                                        ? "var(--copper-subtle, rgba(200,121,65,0.10))"
                                        : "var(--bg-surface, rgba(255,252,247,0.72))",
                                    border: `2px dashed ${dragOver ? "var(--copper, #c87941)" : "var(--glass-border, rgba(0,0,0,0.12))"}`,
                                }}
                            >
                                <Upload size={28} className="mx-auto mb-3 text-[color:var(--copper,#c87941)]" />
                                <p className="font-display text-base text-[color:var(--slate-deep,#1F2D40)] mb-1 break-words">
                                    {file ? file.name : (pt.invoiceModal?.dropHint || "Drop your invoice PDF here")}
                                </p>
                                <p className="font-body text-xs text-[color:var(--slate-mid,#5B6B7E)]">
                                    {pt.invoiceModal?.orClick || "or click to choose a file"}
                                </p>
                                <input
                                    ref={fileInputRef} type="file" accept="application/pdf"
                                    className="hidden" onChange={onPick}
                                    data-testid="invoice-file-input"
                                />
                            </div>

                            {/* Progress bar */}
                            {(uploading || progress > 0) && (
                                <div className="space-y-1.5" data-testid="invoice-progress-wrap">
                                    <div className="w-full h-1.5 rounded-full overflow-hidden"
                                         style={{ background: "var(--bg-surface, rgba(0,0,0,0.06))" }}>
                                        <div className="h-full transition-all duration-300 ease-out"
                                             style={{ width: `${progress}%`, background: "var(--copper, #c87941)" }}
                                             data-testid="invoice-progress-bar" />
                                    </div>
                                    <p className="font-body text-[11px] text-[color:var(--slate-mid,#5B6B7E)] flex items-center gap-1.5">
                                        {uploading && <Loader2 size={11} className="animate-spin" />}
                                        {uploading ? (pt.invoiceModal?.extracting || "Extracting fields with Claude…")
                                                  : (pt.invoiceModal?.uploadDone || "Done.")}
                                    </p>
                                </div>
                            )}

                            {error && (
                                <div className="rounded-lg px-3 py-2 text-sm"
                                     style={{ background: "rgba(220, 38, 38, 0.10)", color: "#b91c1c" }}
                                     data-testid="invoice-error">
                                    {error}
                                </div>
                            )}

                            {/* Actions */}
                            {result && (
                                <div className="space-y-2 pt-2">
                                    {result.ticket_id ? (
                                        <button
                                            onClick={openTicket}
                                            data-testid="invoice-open-ticket"
                                            className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-display transition"
                                            style={{ background: "var(--copper, #c87941)", color: "white" }}
                                        >
                                            <TicketCheck size={16} />
                                            {pt.invoiceModal?.openTicket || "View ticket"} · {result.ticket_number}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={createTicket}
                                            disabled={creatingTicket || result.status !== "extracted"}
                                            data-testid="invoice-create-ticket"
                                            className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-display transition disabled:opacity-50"
                                            style={{ background: "var(--copper, #c87941)", color: "white" }}
                                        >
                                            {creatingTicket ? <Loader2 size={16} className="animate-spin" /> : <TicketCheck size={16} />}
                                            {pt.invoiceModal?.createTicket || "Create ticket from invoice"}
                                        </button>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <button onClick={copyJson} data-testid="invoice-copy-json"
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs"
                                                style={{ background: "var(--bg-surface, rgba(255,252,247,0.72))",
                                                         color: "var(--slate-deep, #1F2D40)",
                                                         border: "1px solid var(--glass-border, rgba(0,0,0,0.12))" }}>
                                            <Copy size={12} /> {pt.invoiceModal?.copyJson || "Copy JSON"}
                                        </button>
                                        <button onClick={downloadJson} data-testid="invoice-download-json"
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs"
                                                style={{ background: "var(--bg-surface, rgba(255,252,247,0.72))",
                                                         color: "var(--slate-deep, #1F2D40)",
                                                         border: "1px solid var(--glass-border, rgba(0,0,0,0.12))" }}>
                                            <Download size={12} /> {pt.invoiceModal?.downloadJson || "Download JSON"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: structured fields */}
                        <div className="lg:col-span-3 p-6 space-y-5"
                             style={{ background: "var(--bg-base, #faf7f2)" }}
                             data-testid="invoice-result-panel">
                            {!result && !uploading && (
                                <div className="grid place-items-center py-16">
                                    <p className="text-center font-body text-sm text-[color:var(--slate-mid,#5B6B7E)] max-w-xs">
                                        {pt.invoiceModal?.emptyHint
                                            || "Drop an invoice PDF on the left to see vendor, totals, line items and tax rate appear here."}
                                    </p>
                                </div>
                            )}

                            {result && (
                                <>
                                    <div className="flex items-center justify-between gap-3 pb-3"
                                         style={{ borderBottom: "1px solid var(--glass-border, rgba(0,0,0,0.08))" }}>
                                        <div className="space-y-0.5">
                                            <h4 className="font-display text-base text-[color:var(--slate-deep,#1F2D40)]">
                                                {extracted.vendor_name || "—"}
                                            </h4>
                                            <p className="font-mono text-xs text-[color:var(--slate-mid,#5B6B7E)]">
                                                {extracted.invoice_number || "—"}
                                            </p>
                                        </div>
                                        <_StatusPill flags={flags} />
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                        <_KV label={pt.invoiceModal?.vendorTaxId || "Vendor NIF"} value={extracted.vendor_tax_id} mono testId="kv-vendor-nif" />
                                        <_KV label={pt.invoiceModal?.currency || "Currency"} value={extracted.currency} mono />
                                        <_KV label={pt.invoiceModal?.issueDate || "Issue date"} value={extracted.issue_date} mono testId="kv-issue-date" />
                                        <_KV label={pt.invoiceModal?.dueDate || "Due date"} value={extracted.due_date} mono testId="kv-due-date" />
                                        <_KV label={pt.invoiceModal?.subtotal || "Subtotal"} value={_fmtMoney(extracted.subtotal, extracted.currency)} mono />
                                        <_KV label={pt.invoiceModal?.tax || "Tax"} value={
                                            `${_fmtMoney(extracted.tax_amount, extracted.currency)}${extracted.tax_rate_pct != null ? ` (${extracted.tax_rate_pct}%)` : ""}`
                                        } mono />
                                        <_KV label={pt.invoiceModal?.total || "Total"} value={_fmtMoney(extracted.total_amount, extracted.currency)} mono testId="kv-total" />
                                        <_KV label={pt.invoiceModal?.paymentTerms || "Payment terms"} value={extracted.payment_terms} />
                                    </div>

                                    {(extracted.vendor_address || extracted.customer_name) && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                            <_KV label={pt.invoiceModal?.vendorAddress || "Vendor address"} value={extracted.vendor_address} />
                                            <_KV label={pt.invoiceModal?.customer || "Customer"} value={
                                                (extracted.customer_name || "—") + (extracted.customer_tax_id ? ` (NIF ${extracted.customer_tax_id})` : "")
                                            } />
                                        </div>
                                    )}

                                    {lineItems.length > 0 && (
                                        <div className="space-y-2 pt-2">
                                            <p className="font-display text-sm text-[color:var(--slate-deep,#1F2D40)]">
                                                {pt.invoiceModal?.lineItems || "Line items"} · {lineItems.length}
                                            </p>
                                            <div className="rounded-lg overflow-hidden"
                                                 style={{ border: "1px solid var(--glass-border, rgba(0,0,0,0.12))" }}
                                                 data-testid="invoice-line-items">
                                                <table className="w-full text-xs">
                                                    <thead style={{ background: "var(--bg-surface, rgba(0,0,0,0.04))" }}>
                                                        <tr>
                                                            <th className="text-left px-3 py-2 font-body text-[10px] uppercase tracking-wider text-[color:var(--slate-light,#94a0b1)]">
                                                                {pt.invoiceModal?.colDescription || "Description"}
                                                            </th>
                                                            <th className="text-right px-3 py-2 font-body text-[10px] uppercase tracking-wider text-[color:var(--slate-light,#94a0b1)]">
                                                                {pt.invoiceModal?.colQty || "Qty"}
                                                            </th>
                                                            <th className="text-right px-3 py-2 font-body text-[10px] uppercase tracking-wider text-[color:var(--slate-light,#94a0b1)]">
                                                                {pt.invoiceModal?.colUnit || "Unit price"}
                                                            </th>
                                                            <th className="text-right px-3 py-2 font-body text-[10px] uppercase tracking-wider text-[color:var(--slate-light,#94a0b1)]">
                                                                {pt.invoiceModal?.colTotal || "Line total"}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {lineItems.map((it, i) => (
                                                            <tr key={i} className="border-t"
                                                                style={{ borderColor: "var(--glass-border, rgba(0,0,0,0.08))" }}>
                                                                <td className="px-3 py-2 text-[color:var(--slate-deep,#1F2D40)]">{it.description || "—"}</td>
                                                                <td className="px-3 py-2 text-right font-mono text-[color:var(--slate-mid,#5B6B7E)]">{it.quantity ?? "—"}</td>
                                                                <td className="px-3 py-2 text-right font-mono text-[color:var(--slate-mid,#5B6B7E)]">{_fmtMoney(it.unit_price, extracted.currency)}</td>
                                                                <td className="px-3 py-2 text-right font-mono text-[color:var(--slate-deep,#1F2D40)]">{_fmtMoney(it.line_total, extracted.currency)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {flags.length > 0 && (
                                        <div className="space-y-1.5 pt-2">
                                            <p className="font-display text-sm text-[color:var(--slate-deep,#1F2D40)]">
                                                {pt.invoiceModal?.flagsHeader || "Validation"}
                                            </p>
                                            <ul className="space-y-1.5">
                                                {flags.map((f, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-xs"
                                                        data-testid={`invoice-flag-${f.severity}`}>
                                                        <AlertCircle size={12}
                                                            className={f.severity === "error" ? "text-[#b91c1c]" : "text-[#b45309]"} />
                                                        <span className="text-[color:var(--slate-deep,#1F2D40)]">{f.message}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceUploadModal;
